import {
  Box,
  Flex,
  Text,
  Image,
  Divider,
  VStack,
  Stack,
  Button,
} from "@chakra-ui/react";
import QRCode from "react-qr-code";
import Barcode from "react-barcode";
import { FC, useEffect, useState } from "react";
import logoPadrao from "../../../../assets/images/logo-alt.png";
import logoAzul from "../../../../assets/images/logo_itau_personnalite_92x92.png";
import { toast } from "react-toastify";
import "./print.css";
import { IconButton, Tooltip } from "@chakra-ui/react";
import { CopyIcon, CheckIcon, Icon } from "@chakra-ui/icons";
import { BLUEMONEDD, GRAY, ORANGEMONEDD } from "../../../../constants/colors";
import { FaSearchPlus, FaSearchMinus, FaPrint, FaBarcode, FaBolt, FaBookmark, FaCalendar, FaCalendarAlt, FaDollarSign, FaFlag, FaFlagCheckered, FaMoneyBill, FaPlus, FaQrcode, FaRegBookmark, FaRegCalendar } from "react-icons/fa";
import { CNPJFormater, CPFFormater } from "../../../../defaultFunctions/formaters";

// Boleto exibido como MODELO na tela de configuração (Gestão de Boletos).
//
// É o mesmo desenho do boleto real do admin (cadastros/boletos/Boleto.tsx) —
// mesma guia do pagador, mesmos campos, mesma formatação — para o lojista ver
// exatamente o que o pagador recebe enquanto escolhe logo e cor.
//
// Duas diferenças, e só elas: os dados são de exemplo (não busca boleto nenhum)
// e a logo/cor vêm das props, para o preview reagir ao que está sendo editado.
// Verificação por CPF e botão de imprimir não existem aqui: são do boleto real.

interface BoletoReq {
  id: string;
  description: string;
  amount: string;
  dueDate: string;
  beneficiario: string | null;
  cpfBeneficiario: string | null;
  pagador: string;
  cpfPagador: string;
  digitableLine: string;
  barcode: string;
  dataEmissao: string;
  documentNumber: string;
  emv: string;
  recebedorTipoDoc: boolean;
  recebedorRazao: string;
  recebedorFantasia: string;
  recebedorDocumento: string;
  dataLimitePagamento: string | null;
  urlLogo: string | null;
  cor1: string | null;
  cor2: string | null;
  bolepixTaxas: Array<any> | null;
}

interface bolepixTaxa {
  idBolepix: string;
  tipoLancamento: string;
  mode: string;
  amount: number;
  percentage: number;
  diasAposVencimento: number;
  diaLimiteDesconto: number;
  frequency: string;
}

interface BoletoData {
  description: string;
  amountBRL: string;
  dueDate: string;
  beneficiario: string;
  cpfBeneficiario: string;
  pagador: string;
  cpfPagador: string;
  digitableLine: string;
  barcode: string;
  createdAt: string;
  documentNumber: string;
  agenciaConta: string;
  qrcodeId: string;
  updatedAt: string;
  amount: string;
  emv: string;
  multa: string;
  juros: string;
  carteira: string;
  especie: string;
  descontoValor: string;
  descontoDataLimite: string;
  instrucoes: string | null;
  abatimentos: string;
  outrasDeducoes: string;
  mora: string;
  outrosAcrescimos: string;
  quantity: string;
  recebedorRazao: string;
  recebedorFantasia: string;
  recebedorDocumento: string;
  bankCode: string;
  urlLogo: string | null;
  headerColor: string | null;
  cor1: string | null;
  cor2: string | null;
  bolepixTaxas: Array<any> | null;
}

interface BoletoProps {
  logo?: string;
  emailMessage?: string;
  smsMessage?: string;
  whatsAppMessage?: string;
  notificationEnabled?: boolean;
  colorHeader?: string;
}

const BoletoComponent: FC<BoletoProps> = ({ logo, colorHeader }) => {
  const [boletoData, setBoletoData] = useState<BoletoData>({
    description: "-",
    amountBRL: "-",
    dueDate: "-",
    beneficiario: "-",
    cpfBeneficiario: "-",
    pagador: "-",
    cpfPagador: "-",
    digitableLine: "-",
    barcode: "-",
    createdAt: "-",
    documentNumber: "-",
    agenciaConta: "-",
    qrcodeId: "-",
    updatedAt: "-",
    amount: "-",
    emv: "-",
    multa: "-",
    juros: "-",
    carteira: "-",
    especie: "-",
    descontoValor: "-",
    descontoDataLimite: "-",
    instrucoes: "-",
    abatimentos: "-",
    outrasDeducoes: "-",
    mora: "-",
    outrosAcrescimos: "-",
    quantity: "-",
    recebedorRazao: "-",
    recebedorFantasia: "-",
    recebedorDocumento: "-",
    bankCode: "-",
    urlLogo: null,
    headerColor: null,
    cor1: null,
    cor2: null,
    bolepixTaxas: null,
  });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedPix, setCopiedPix] = useState(false);
  const [juros, setJuros] = useState<bolepixTaxa | null>(null);
  const [multa, setMulta] = useState<bolepixTaxa | null>(null);
  const [desconto, setDesconto] = useState<bolepixTaxa | null>(null);

  // Estados do modal
  /**
   * Verificação por CPF/CNPJ do pagador. Quando ela virar opcional, este é o
   * único ponto do front a mexer — mas não basta: o `view/{id}` do core valida
   * o `secret` e recusa sem ele, então o backend precisa aceitar a leitura
   * aberta antes de desligar isto aqui.
   */
  // preview não pede verificação: quem está aqui já é o dono do boleto
  const EXIGE_VERIFICACAO = false;


  // ─── Zoom da tela ──────────────────────────────────────────────────────────
  //
  // O boleto é uma prancha FIXA de 1060px (as posições dos campos dependem
  // disso). Com o zoom do navegador em 150%/200% a viewport encolhe abaixo de
  // 1060 e a prancha estoura para fora — é o "desconfigurado" que o cadastro
  // relatou. Bloquear o zoom NÃO resolve: Ctrl+ não é bloqueável e tirar o
  // zoom quebraria quem depende dele para enxergar.
  //
  // Em vez disso a página tem zoom PRÓPRIO, que respeita a prancha: entra já
  // ajustada à janela (nunca acima de 100%) e os botões mudam a escala sem
  // deformar nada. Usa a propriedade `zoom` (e não `transform: scale`) porque
  // ela reflete no layout — com transform sobraria barra de rolagem fantasma.
  const LARGURA_BOLETO = 1060;
  const [escala, setEscala] = useState(1);
  const [escalaManual, setEscalaManual] = useState(false);

  useEffect(() => {
    const ajustarNaJanela = () => {
      // 32px = respiro nas laterais, para o boleto não colar na borda
      const disponivel = document.documentElement.clientWidth - 32;
      setEscala(Math.min(1, disponivel / LARGURA_BOLETO));
    };

    ajustarNaJanela();

    // Depois que o operador escolhe uma escala, parar de reajustar: senão o
    // próprio Ctrl+ (que muda o clientWidth) desfaria a escolha dele.
    const aoRedimensionar = () => {
      if (!escalaManual) ajustarNaJanela();
    };

    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, [escalaManual]);

  const mudarEscala = (delta: number) => {
    setEscalaManual(true);
    setEscala((atual) => Math.min(2, Math.max(0.4, Number((atual + delta).toFixed(2)))));
  };

  const ajustarALargura = () => {
    setEscalaManual(false);
    const disponivel = document.documentElement.clientWidth - 32;
    setEscala(Math.min(1, disponivel / LARGURA_BOLETO));
  };

  const [isModalOpen, setIsModalOpen] = useState(EXIGE_VERIFICACAO);

  /** conteúdo do boleto só é montado depois que a verificação passa */
  const conteudoLiberado = !isModalOpen;

  const textColorBlue = "#114AA0";

  useEffect(() => {
    carregarExemplo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exemplo fixo: valores plausíveis para o desenho ficar completo, incluindo
  // juros, multa e desconto — que são justamente os blocos que só aparecem
  // quando existem.
  const carregarExemplo = () => {
    const exemplo: BoletoReq = {
      id: "151a045c-7dd3-4fb4-ebd2-08dde59ff84a",
      description: "Modelo de Boleto - parcela 1 de 1",
      amount: "150.00",
      dueDate: "2026-09-30T00:00:00",
      beneficiario: null,
      cpfBeneficiario: null,
      pagador: "Nome do Pagador",
      cpfPagador: "99999999999",
      digitableLine: "34191098030000150000109802439838893313392100",
      barcode: "34191113700000005001098024398388933133921000",
      dataEmissao: "2026-08-21T10:00:00",
      documentNumber: "000001",
      emv: "00020101021226770014BR.GOV.BCB.PIX2555api.itau/pix/qr/v2/6402b1e2-1619-49c2-91b6-b5fb04a1a4585204000053039865802BR5911ZOOP BRASIL6006OSASCO62070503***6304B2D1",
      recebedorTipoDoc: true,
      recebedorRazao: "SUA EMPRESA LTDA - CNPJ: 00000000000191",
      recebedorFantasia: "SUA EMPRESA",
      recebedorDocumento: "00000000000191",
      dataLimitePagamento: "2026-10-30T00:00:00",
      urlLogo: null,
      cor1: null,
      cor2: null,
      bolepixTaxas: [
        { idBolepix: "", tipoLancamento: "J", mode: "PERCENTAGE", amount: 0, percentage: 1, diasAposVencimento: 1, diaLimiteDesconto: 0, frequency: "MONTHLY" },
        { idBolepix: "", tipoLancamento: "M", mode: "PERCENTAGE", amount: 0, percentage: 2, diasAposVencimento: 1, diaLimiteDesconto: 0, frequency: "" },
        { idBolepix: "", tipoLancamento: "D", mode: "FIXED", amount: 5, percentage: 0, diasAposVencimento: 0, diaLimiteDesconto: 2, frequency: "" },
      ],
    };

    const amount = Number(exemplo.amount);

    setBoletoData({
      description: exemplo.description,
      amountBRL: `R$ ${amount.toFixed(2).replace(".", ",")}`,
      dueDate: new Date(exemplo.dueDate).toLocaleDateString("pt-BR"),
      beneficiario: exemplo.beneficiario || "-",
      cpfBeneficiario: exemplo.cpfBeneficiario || "-",
      recebedorRazao: exemplo.recebedorRazao,
      recebedorFantasia: exemplo.recebedorFantasia,
      recebedorDocumento: exemplo.recebedorDocumento,
      pagador: exemplo.pagador,
      cpfPagador: exemplo.cpfPagador,
      digitableLine: exemplo.digitableLine,
      barcode: exemplo.barcode,
      createdAt: new Date(exemplo.dataEmissao).toLocaleDateString("pt-BR"),
      documentNumber: exemplo.documentNumber,
      agenciaConta: "",
      qrcodeId: "",
      updatedAt: exemplo.dataLimitePagamento
        ? new Date(exemplo.dataLimitePagamento).toLocaleDateString("pt-BR")
        : "NULL",
      amount: amount.toFixed(2).replace(".", ","),
      emv: exemplo.emv,
      multa: "",
      juros: "",
      carteira: exemplo.barcode ? exemplo.barcode.substring(5, 7) : "-",
      especie: "",
      descontoValor: "",
      descontoDataLimite: "",
      instrucoes: "",
      abatimentos: "",
      outrasDeducoes: "",
      mora: "",
      outrosAcrescimos: "",
      quantity: "",
      bankCode: "341",
      urlLogo: null,
      headerColor: null,
      cor1: null,
      cor2: null,
      bolepixTaxas: exemplo.bolepixTaxas,
    });

    exemplo.bolepixTaxas?.forEach((taxa) => {
      if (taxa.tipoLancamento === "J") setJuros(taxa);
      if (taxa.tipoLancamento === "M") setMulta(taxa);
      if (taxa.tipoLancamento === "D") setDesconto(taxa);
    });

    setLoading(false);
  };

  /**
   * Linha digitável no formato que se lê num boleto:
   *   AAAAA.AAAAA BBBBB.BBBBBB CCCCC.CCCCCC D EEEEEEEEEEEEEE
   * Sem isso são 47 dígitos colados, que ninguém consegue conferir nem digitar.
   */
  const formatarLinhaDigitavel = (linha: string) => {
    const d = (linha ?? "").replace(/\D/g, "");
    if (d.length !== 47) return linha; // formato inesperado: mostra como veio

    return (
      `${d.slice(0, 5)}.${d.slice(5, 10)} ` +
      `${d.slice(10, 15)}.${d.slice(15, 21)} ` +
      `${d.slice(21, 26)}.${d.slice(26, 32)} ` +
      `${d.slice(32, 33)} ` +
      `${d.slice(33)}`
    );
  };

  /**
   * Quem registra o título no Itaú (via Zoop) é a Monedd — por isso ela é a
   * beneficiária impressa. O lojista, credor real da cobrança, vai no
   * Sacador/Avalista logo abaixo. Antes só o CNPJ aparecia, cravado no meio do
   * JSX e sem o nome: era o "nome da Monedd some ao abrir o boleto".
   */
  const BENEFICIARIO = {
    nome: "MONEDD GESTAO, SERVICOS E MEIOS DE PAGAMENTOS LTDA",
    documento: "32505213000161",
  };

  /** A view devolve recebedorRazao já com " - CNPJ: ..." grudado; aqui fica só o nome. */
  const nomeDoLojista = () => {
    const razao = (boletoData.recebedorRazao ?? "").split(" - CNPJ:")[0].trim();
    return razao || boletoData.recebedorFantasia || "-";
  };

  /** "até o vencimento" / "até 18/08/2026 (2 dias antes do vencimento)" */
  const textoLimiteDesconto = (diasAntes: number) => {
    if (!diasAntes || diasAntes <= 0) return "o vencimento";

    const [dia, mes, ano] = (boletoData.dueDate ?? "").split("/");
    if (dia && mes && ano) {
      const limite = new Date(Number(ano), Number(mes) - 1, Number(dia));
      limite.setDate(limite.getDate() - diasAntes);
      const data = limite.toLocaleDateString("pt-BR");
      return `${data} (${diasAntes} ${diasAntes === 1 ? "dia" : "dias"} antes do vencimento)`;
    }

    return `${diasAntes} ${diasAntes === 1 ? "dia" : "dias"} antes do vencimento`;
  };

  const formatarDocumento = (doc: string) => {
    const d = (doc ?? "").replace(/\D/g, "");
    if (d.length === 11) return CPFFormater(d);
    if (d.length === 14) return CNPJFormater(d);
    return doc || "-";
  };

  const translateFrequency = (frequency: string) => {
    switch (frequency) {
      case "DAILY":
        return "ao dia";
      case "MONTHLY":
        return "ao mês";
      // a Zoop escreve com dois N; o "ANUALLY" antigo caía no default e
      // imprimia a sigla crua no boleto
      case "ANNUALLY":
        return "ao ano";
      default:
        return frequency;
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(boletoData.digitableLine).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  function getParcelas(descricao: string): string {
    if (!descricao) {
      return "1 de 1";
    }

    const padrao = /parcela (\d+ de \d+)/i;
    const resultado = descricao.match(padrao);

    if (resultado && resultado[1]) {
      return resultado[1];
    }

    return "1 de 1";
  }

  // const handleCopyPix = () => {
  //   navigator.clipboard.writeText(boletoData.emv).then(() => {
  //     setCopiedPix(true);
  //     setTimeout(() => setCopiedPix(false), 2000);
  //   });
  // };

  return (
    <>

      {/* Conteúdo do Boleto — só é montado depois da verificação.
          Antes ficava sempre no DOM, atrás do ModalOverlay (que é translúcido):
          dava para ler o boleto por trás do modal, e os dados iam para o HTML da
          página mesmo sem ninguém digitar nada. Esconder por opacidade não
          resolveria isso — por isso o conteúdo simplesmente não existe até a
          liberação. */}
      {conteudoLiberado && (
      /* O index.css trava html/body/#root em overflow:hidden — o app rola pela
         coluna interna dele. Esta página é avulsa e não tem essa coluna, então
         o boleto passava da viewport sem ter onde rolar. Aqui ela ganha o
         próprio scroll. */
      <Box className="pagina-boleto" h="100vh" overflow="auto" w="100%">
        {/* Barra de ferramentas da tela — fora da impressão (no-print), senão
            os próprios botões sairiam no boleto. Acompanha a largura da
            prancha escalada para não ficar desalinhada dela. */}
        <Flex
          className="no-print"
          w="100%"
          maxW={`${LARGURA_BOLETO * escala}px`}
          mx="auto"
          justify="flex-end"
          align="center"
          gap={2}
          pt={4}
        >
          <Flex align="center" gap={1} borderWidth="1px" borderRadius="md" px={1} py={0.5} bg="white">
            <IconButton
              aria-label="Diminuir zoom"
              icon={<FaSearchMinus />}
              size="xs"
              variant="ghost"
              onClick={() => mudarEscala(-0.1)}
              isDisabled={escala <= 0.4}
            />
            <Text fontSize="xs" minW="42px" textAlign="center" fontWeight="bold">
              {Math.round(escala * 100)}%
            </Text>
            <IconButton
              aria-label="Aumentar zoom"
              icon={<FaSearchPlus />}
              size="xs"
              variant="ghost"
              onClick={() => mudarEscala(0.1)}
              isDisabled={escala >= 2}
            />
            <Button size="xs" variant="ghost" onClick={ajustarALargura}>
              Ajustar
            </Button>
          </Flex>
          <Button
            leftIcon={<FaPrint />}
            size="sm"
            bgColor={textColorBlue}
            color="white"
            _hover={{ opacity: 0.85 }}
            onClick={() => window.print()}
          >
            Imprimir boleto
          </Button>
        </Flex>
      <Box
        id="boleto-container"
        bg="white"
        p={0}
        // paddingTop="0px"
        w="1060px"
        mx="auto"
        marginY={4}
        border="1px solid #ccc"
        // `zoom` (e nao transform) porque ela encolhe a CAIXA junto com o
        // conteudo: com transform sobraria a largura antiga reservada e uma
        // barra de rolagem horizontal fantasma. Na impressao o print.css
        // sobrescreve com !important, entao a escala da tela nao vaza pro papel.
        sx={{ zoom: escala }}
      // borderRadius="md"
      >
        <Box
          bg={colorHeader ? colorHeader : "#20296D"}
          color="white"

          // borderBottomRadius={14}
          h={16}
        >

        </Box>

        <Box
          // p={8}
          pt={4}
          mt={4}
          // ml={4}
          // mr={4}
          bg="white"
          borderRadius="md"
          borderStartRadius={14}
          borderEndRadius={20}
        >
          <Flex
            px={8}
            justify="space-between"
            flexWrap="wrap"
            rowGap={2}
            alignItems="center"
          >
            <Flex h="100%">
              <Image
                src={logo ? logo : logoPadrao}
                // src={logo}
                alt="Boleto Logo"
                height="45px"
                maxH="45px"
                maxW="300px"
                objectFit="contain"
                objectPosition="left center"
              />
              <Flex w={1} minH="100%" bg="white" mr={4}>
                <></>
              </Flex>
              {/* <Flex flexDir="column" h="100%" justifyContent="space-between">
                <Text fontSize="lg" fontWeight="bold">
                  {boletoData.description}
                </Text>
              </Flex> */}
            </Flex>
          </Flex>
          <Flex
            px={8}
            justify="space-between"
            flexWrap="wrap"
            rowGap={3}
            mt={10}
            alignItems="center"
          >
            <Flex minW="280px" minH={"100px"} align="center" backgroundColor={"#FEEDDF"} p={2} borderRadius="md" mt={4}>
              <Flex
                w="72px"
                h="72px"
                bg={"#F47C27"}
                borderRadius="50%"
                alignItems="center"
                justifyContent="center"
                mr={6}
                ml={4}
              >
                <FaRegBookmark color="white" size="28px" />
              </Flex>
              <Flex flexDir="column">
                <Text fontSize="1.4rem">Parcela</Text>
                <Text fontSize="1.4rem" fontWeight="bold">{getParcelas(boletoData.description)}</Text>
              </Flex>
            </Flex>
            <Flex minW="280px" minH={"100px"} align="center" backgroundColor={"#FEEDDF"} p={2} borderRadius="md" mt={4}>
              <Flex
                w="72px"
                h="72px"
                bg={"#F47C27"}
                borderRadius="50%"
                alignItems="center"
                justifyContent="center"
                mr={6}
                ml={4}
              >
                <FaDollarSign color="white" size="28px" />
              </Flex>
              <Flex flexDir="column">
                <Text fontSize="1.4rem">Valor</Text>
                <Text fontSize="1.4rem" fontWeight="bold">{boletoData.amountBRL}</Text>
              </Flex>
            </Flex>
            <Flex minW="280px" minH={"100px"} align="center" backgroundColor={"#FEEDDF"} p={2} borderRadius="md" mt={4}>
              <Flex
                w="72px"
                h="72px"
                bg={"#F47C27"}
                borderRadius="50%"
                alignItems="center"
                justifyContent="center"
                mr={6}
                ml={4}
              >
                <FaRegCalendar color="white" size="28px" />
              </Flex>
              <Flex flexDir="column">
                <Text fontSize="1.4rem">Vencimento</Text>
                <Text fontSize="1.4rem" fontWeight="bold">{boletoData.dueDate}</Text>
              </Flex>
            </Flex>
          </Flex>
          {/* <Flex
            direction="row"
            mt={4}
            justify="space-between"
            gap={2}
            minW="600px"
          >
            <VStack spacing={0} align="flex-start">
              <Text fontSize="sm">Valor</Text>
              <Text fontWeight="bold">{boletoData.amountBRL}</Text>
            </VStack>
            <VStack spacing={0} align="flex-start">
              <Text fontSize="sm">Vencimento</Text>
              <Text fontWeight="bold">{boletoData.dueDate}</Text>
            </VStack>
          </Flex> */}

          {/* <Divider my={4} /> */}

          <Stack
            px={8}
            mt={4}
            direction="row"
            justifyContent="space-between"
            spacing={8}
            wrap="wrap"
            minW="600px"
          >
            <VStack align="start" spacing={1} flex={2}>
              <Text fontSize="md" color={textColorBlue}>
                Nome do Beneficiário
              </Text>
              <Text color={GRAY}>MONEDD GESTAO, SERVICOS E MEIOS DE PAGAMENTOS LTDA</Text>
              <Text fontSize="md" color={GRAY}>CPF/CNPJ: 32.505.213/0001-61</Text>
            </VStack>
            <VStack align="start" spacing={1} flex={1}>
              <Text fontSize="md" color={textColorBlue}>
                Nome do Recebedor
              </Text>
              <Text fontSize={"md"} color={GRAY}>{boletoData.recebedorRazao}</Text>
            </VStack>
          </Stack>
          {/* Cabeçalho "Como Pagar": ajuda na TELA, mas no papel só repete o
              código de barras e a linha digitável que já saem nas duas vias.
              Fica fora da impressão — é o que permite manter o zoom alto e o
              código de barras grande o suficiente para ser lido no caixa. */}
          <Flex
            className="no-print"
            direction="column"
            w="100%"
            justify="center"
            mt={2}
            px={4}
            pb={2}
          >
            <Divider height="1px" bg={textColorBlue} />
            <Flex gap={4} align="center" w="100%" bg={"#F2F9FC"} px={6} py={4}>

              <Flex direction="column" w="100%" justify="start" align="start" flexWrap="wrap" gap={3}>
                <Flex w="100%" align="start" justify={"start"}>
                  <Text fontSize="1.1rem" fontWeight="bold" color={textColorBlue}>Como Pagar</Text>
                </Flex>
                <Box flex={1}>
                  <Flex align="center">
                    <Flex mr={2} bg={"#CAE9F9"} p={2} borderRadius="50%" align="center" justify="center">
                      <FaBarcode size={14} color={textColorBlue} />
                    </Flex>

                    <Text fontSize="sm" color={GRAY} fontWeight={"bold"}>Escaneie o código de barras</Text>
                  </Flex>

                  <Box mt={2} w="100%">
                    <Barcode
                      value={boletoData.barcode}
                      width={1.3}
                      height={38}
                      displayValue={false}
                      margin={0}
                    />
                  </Box>

                  {/* Linha digitável por extenso, logo abaixo do código de
                      barras: é o caminho de quem não consegue escanear —
                      digitar no app do banco. Formatada em blocos, senão são 47
                      dígitos colados que ninguém confere. */}
                  <Box mt={2} w="100%">
                    <Text fontSize="xs" color={GRAY} fontWeight="bold" mb={1}>
                      Ou copie a linha digitável
                    </Text>
                    <Flex align="center" gap={2} flexWrap="wrap">
                      <Text
                        fontSize="md"
                        fontWeight="bold"
                        letterSpacing="0.5px"
                        wordBreak="break-word"
                      >
                        {formatarLinhaDigitavel(boletoData.digitableLine)}
                      </Text>
                      <Tooltip
                        className="no-print"
                        label={copied ? "Copiado!" : "Copiar"}
                        placement="top"
                      >
                        <IconButton
                          className="no-print"
                          size="sm"
                          aria-label="Copiar linha digitável"
                          icon={copied ? <CheckIcon /> : <CopyIcon />}
                          onClick={handleCopy}
                          variant="ghost"
                          color={textColorBlue}
                        />
                      </Tooltip>
                    </Flex>
                  </Box>

                  {/* <Text fontSize="xs" mt={2}>
                ----------------------------------------- OU
                -----------------------------------------
              </Text>
              <Text fontSize="sm" mt={1}>
                Copie a linha digitável:
              </Text>
              <Flex align="center" gap={2}>
                <Text fontSize="sm" fontWeight="bold" wordBreak="break-word">
                  {boletoData.digitableLine}
                </Text>
                <Tooltip
                  className="no-print"
                  label={copied ? "Copiado!" : "Copiar"}
                  placement="top"
                >
                  <IconButton
                    className="no-print"
                    size="sm"
                    aria-label="Copiar linha digitável"
                    icon={copied ? <CheckIcon /> : <CopyIcon />}
                    onClick={handleCopy}
                    variant="ghost"
                  />
                </Tooltip>
              </Flex> */}
                  {/* <Text fontSize="sm" mt={1}>
                Copia e Cola Pix:
              </Text> */}
                </Box>


              </Flex>
              {/* QR Code PIX removido a pedido do cliente
              <Box flexShrink={0} mt={10}>
                <Flex align="center">
                  <Flex mr={2} bg={"#CAE9F9"} p={2} borderRadius="50%" align="center" justify="center">
                    <FaQrcode size={14} color={textColorBlue} />
                  </Flex>

                  <Text fontSize="sm" color={GRAY} fontWeight={"bold"}>Pagamento via pix</Text>
                </Flex>
                <Flex align="center" justify="center" border={"2px solid #CAE9F9"} mt={2} p={2} bg={"#ffffff"} borderRadius="md">
                  <QRCode value={boletoData.emv} size={140} />
                </Flex>
              </Box>
              */}
            </Flex>
          </Flex>



          {/* <Flex justify="center" align="center" mt={4} mb={4} gap={2}>
            <Flex className="visible-print">
              <Text>{boletoData.emv}</Text>
            </Flex>
            <Tooltip
              className="no-print"
              label={copiedPix ? "Copiado!" : "Copiar"}
              placement="top"
            >
              <IconButton
                className="no-print"
                size="sm"
                aria-label="Copiar linha digitável"
                icon={copiedPix ? <CheckIcon /> : <CopyIcon />}
                onClick={handleCopyPix}
                variant="ghost"
              />
            </Tooltip>
          </Flex> */}

          {/* ===================== RECIBO DO PAGADOR =====================
              A via que se destaca no tracejado abaixo e fica com quem pagou —
              é o comprovante dele até a compensação. A tela só tinha a Ficha de
              Compensação; o divisor tracejado já existia, mas sem nada acima. */}
          <Box fontSize="xs" border="1px solid black" m={4}>
            <Flex borderBottom="1px solid black" align="center">
              <Flex
                borderRight="1px solid black"
                p={1}
                w="25%"
                align="center"
              >
                <Image src={logoAzul} alt="Logo Itaú" height="40px" mr={2} />
                <Text fontSize="0.9rem" fontWeight="bold">BANCO ITAÚ SA</Text>
              </Flex>
              <Box borderRight="1px solid black" p={1} w="12%" textAlign="center">
                <Text fontSize="0.9rem" fontWeight="bold">{boletoData.bankCode}</Text>
              </Box>
              <Box p={1} w="63%" textAlign="right">
                <Text fontSize="0.9rem" fontWeight="bold" letterSpacing="0.5px">
                  {formatarLinhaDigitavel(boletoData.digitableLine)}
                </Text>
              </Box>
            </Flex>

            <Flex borderBottom="1px solid black">
              <Box borderRight="1px solid black" p={1} w="25%">
                <Text color={GRAY}>Vencimento</Text>
                <Text fontWeight="bold">{boletoData.dueDate}</Text>
              </Box>
              <Box borderRight="1px solid black" p={1} w="25%">
                <Text color={GRAY}>Agência/Código do Beneficiário</Text>
                <Text minH={"1rem"}></Text>
              </Box>
              <Box borderRight="1px solid black" p={1} w="25%">
                <Text color={GRAY}>Número do Documento</Text>
                <Text fontWeight="bold">{boletoData.documentNumber}</Text>
              </Box>
              <Box p={1} w="25%">
                <Text color={GRAY}>Valor do Documento</Text>
                <Text fontWeight="bold">R$ {boletoData.amount}</Text>
              </Box>
            </Flex>

            <Flex borderBottom="1px solid black">
              <Box borderRight="1px solid black" p={1} w="50%">
                <Text color={GRAY}>Pagador</Text>
                <Text fontSize="0.7rem" fontWeight="bold" noOfLines={1}>
                  {boletoData.pagador}
                </Text>
                <Text fontSize="0.7rem">
                  CPF/CNPJ: {formatarDocumento(boletoData.cpfPagador)}
                </Text>
              </Box>
              <Box p={1} w="50%">
                <Text color={GRAY}>Sacador/Avalista</Text>
                <Text fontSize="0.7rem" fontWeight="bold" noOfLines={1}>
                  {nomeDoLojista()}
                </Text>
                <Text fontSize="0.7rem">
                  CPF/CNPJ: {formatarDocumento(boletoData.recebedorDocumento)}
                </Text>
              </Box>
            </Flex>

            <Flex>
              <Box borderRight="1px solid black" p={1} w="70%">
                <Text color={GRAY}>Beneficiário</Text>
                <Text fontWeight="bold">{BENEFICIARIO.nome}</Text>
                <Text>CPF/CNPJ: {formatarDocumento(BENEFICIARIO.documento)}</Text>
              </Box>
              <Box p={1} w="30%" textAlign="right">
                <Text color={GRAY}>Autenticação Mecânica</Text>
                <Text fontWeight="bold" minH={"1rem"}>Recibo do Pagador</Text>
              </Box>
            </Flex>
          </Box>

          <Divider
            my={4}
            borderStyle="dashed"
            // borderColor="gray.500"
            borderColor={"#212121"}
            borderWidth="1px"
            w={"100% - 1px"}
          />

          <Box fontSize="xs" border="1px solid black" m={4}>
            <Flex borderBottom="1px solid black">
              <Flex
                borderRight="1px solid black"
                p={1}
                w="25%"
                justifyItems="center"
                align="center"
                alignContent="center"
              >
                <Image src={logoAzul} alt="Logo Itaú" height="60px" mr={2} />
                <Text fontSize="1rem" fontWeight="bold" >BANCO ITAÚ SA</Text>
              </Flex>
              <Box
                borderRight="1px solid black"
                p={1}
                w="15%"
                alignContent="center"
                justifyItems="center"
              >
                <Text fontSize="1rem" fontWeight="bold">
                  {boletoData.bankCode}
                </Text>
              </Box>
              <Box
                p={1}
                textAlign="right"
                w="60%"
                flex={4}
                alignContent="center"
              >
                <Text fontSize="1rem" fontWeight="bold" letterSpacing="0.5px">
                  {formatarLinhaDigitavel(boletoData.digitableLine)}
                </Text>
              </Box>
            </Flex>

            <Flex borderBottom="1px solid black">
              <Box borderRight="1px solid black" p={1} w="70%">
                <Text color={GRAY}>Local de pagamento</Text>
                <Text>
                  Até o vencimento pague em qualquer banco ou correspondente bancário.
                </Text>
              </Box>
              <Box p={1} flex={1} w="30%">
                <Text color={GRAY}>Vencimento</Text>
                <Text fontWeight="bold">{boletoData.dueDate}</Text>
              </Box>
            </Flex>

            <Flex borderBottom="1px solid black">
              <Box borderRight="1px solid black" p={1} w="70%">
                <Text color={GRAY}>Beneficiário</Text>
                <Text fontWeight="bold">{BENEFICIARIO.nome}</Text>
                <Text>CPF/CNPJ: {formatarDocumento(BENEFICIARIO.documento)}</Text>
              </Box>
              <Box p={1} w="30%">
                <Text color={GRAY}>Agência/Conta</Text>
                <Text fontWeight="bold"></Text>
              </Box>
            </Flex>

            <Flex borderBottom="1px solid black">
              <Box borderRight="1px solid black" p={1} w="18%">
                <Text color={GRAY}>Data do documento</Text>
                <Text fontWeight="bold">{boletoData.createdAt}</Text>
              </Box>
              <Box borderRight="1px solid black" p={1} w="18%">
                <Text color={GRAY}>N. do documento</Text>
                <Text fontWeight="bold">{boletoData.documentNumber}</Text>
              </Box>
              <Box borderRight="1px solid black" p={1} w="10%">
                <Text color={GRAY}>Espécie doc.</Text>
                <Text fontWeight="bold"></Text>
              </Box>
              <Box borderRight="1px solid black" p={1} w="8%">
                <Text color={GRAY}>Aceite</Text>
                <Text></Text>
              </Box>
              <Box borderRight="1px solid black" p={1} w="16%">
                <Text color={GRAY}>Data process.</Text>
                <Text fontWeight="bold">{boletoData.createdAt}</Text>
              </Box>
              <Box p={1} w="30%">
                <Text color={GRAY}>Número Documento</Text>
                <Text fontWeight="bold">{boletoData.documentNumber}</Text>
              </Box>
            </Flex>

            <Flex borderBottom="1px solid black">
              <Box w="18%" borderRight="1px solid black" p={1}>
                <Text color={GRAY}>Uso do banco</Text>
                <Text></Text>
              </Box>
              <Box w="9%" borderRight="1px solid black" p={1}>
                <Text color={GRAY}>Carteira</Text>
                <Text></Text>
              </Box>
              <Box w="9%" borderRight="1px solid black" p={1}>
                <Text color={GRAY}>Espécie</Text>
                <Text></Text>
              </Box>
              <Box w="18%" borderRight="1px solid black" p={1}>
                <Text color={GRAY}>Quantidade</Text>
                <Text></Text>
              </Box>
              <Box w="16%" borderRight="1px solid black" p={1}>
                <Text color={GRAY}>Valor</Text>
                <Text>R$ {boletoData.amount}</Text>
              </Box>
              <Box w="30%" p={1}>
                <Text color={GRAY}>(+) Valor Documento</Text>
                <Text>R$ {boletoData.amount}</Text>
              </Box>
            </Flex>

            <Flex borderBottom="1px solid black">
              {/* Ocupava 70% porque ao lado havia a coluna de totais
                  ((-) Descontos/Abatimentos, (+) Mora/Multa, (=) Valor cobrado).
                  Os três saíram: vinham sempre vazios e repetem o que o texto
                  das instruções já diz. */}
              <Box w="100%" p={1}>
                <Text color={GRAY}>
                  Informações de responsabilidade do beneficiário
                </Text>
                {juros || multa ? (
                  <Text>Após o vencimento será aplicado:</Text>
                ) : (
                  <></>
                )}
                {juros && (
                  <Text>
                    - Juros de{" "}
                    <b>
                      {juros.mode.includes("FIXED") ||
                        juros.mode.includes("AMOUNT")
                        ? `R$ ${(juros.amount / 100)
                          .toFixed(2)
                          .replace(".", ",")}`
                        : `${(juros.percentage / 100)
                          .toFixed(2)
                          .replace(".", ",")}%`}
                    </b>{" "}
                    <b>{translateFrequency(juros.frequency)}</b>, calculados a
                    partir do dia seguinte ao vencimento
                  </Text>
                )}
                {multa && (
                  // Multa é penalidade ÚNICA por atraso — não tem frequência.
                  // O texto antigo dizia "com a frequência diária" porque o
                  // gerar-lote grava frequency="DAILY" fixo, o que fazia o
                  // boleto anunciar multa de 2% AO DIA (o teto legal é 2%, uma vez).
                  <Text>
                    - Multa de{" "}
                    <b>
                      {multa.mode.includes("FIXED") ||
                        multa.mode.includes("AMOUNT")
                        ? `R$ ${(multa.amount / 100)
                          .toFixed(2)
                          .replace(".", ",")}`
                        : `${(multa.percentage / 100)
                          .toFixed(2)
                          .replace(".", ",")}%`}
                    </b>{" "}
                    sobre o valor do documento
                  </Text>
                )}
                {desconto && (
                  <Text mt={2}>
                    Desconto de{" "}
                    <b>
                      {desconto.mode.includes("FIXED") ||
                        desconto.mode.includes("AMOUNT")
                        ? `R$ ${(desconto.amount / 100)
                          .toFixed(2)
                          .replace(".", ",")}`
                        : `${(desconto.percentage / 100)
                          .toFixed(2)
                          .replace(".", ",")}%`}
                    </b>{" "}
                    {/* O avulso converte a data-limite em "dias antes do
                        vencimento" na gravação, então 0 = vale até o próprio
                        vencimento. O texto antigo virava "até 0 dias antes do
                        vencimento", que não quer dizer nada para quem paga. */}
                    para pagamento até <b>{textoLimiteDesconto(desconto.diaLimiteDesconto)}</b>.
                  </Text>
                )}
              </Box>
            </Flex>
            <Flex borderBottom="1px solid black">
              <Box w="100%" p={1}>
                <Text color={GRAY}>Pagador CPF/CNPJ</Text>
                <Text>
                  {boletoData.pagador} - CPF/CNPJ: {boletoData.cpfPagador}
                </Text>
              </Box>
            </Flex>
            <Flex h={"4rem"}>
              {/* Sacador/avalista: o lojista, credor real da cobrança. A
                  Monedd registra o título e figura como beneficiária; sem esta
                  linha o lojista não aparecia em lugar nenhum do boleto. */}
              <Box w="50%" p={1} borderRight="1px solid black">
                <Text color={GRAY}>Sacador/Avalista</Text>
                <Text fontWeight="bold" minH={"1rem"}>{nomeDoLojista()}</Text>
                <Text>CPF/CNPJ: {formatarDocumento(boletoData.recebedorDocumento)}</Text>
              </Box>
              <Box w="50%" p={1} textAlign="right">
                <Text color={GRAY}>Autenticação mecânica</Text>
                <Text fontWeight="bold" minH={"1rem"}>
                  Ficha de compensação
                </Text>
              </Box>
            </Flex>
          </Box>
          <Flex justifyContent="start" m={4}>
            <Barcode
              value={boletoData.barcode}
              width={2.3}
              height={50}
              displayValue={false}
            // margin={4}
            />
          </Flex>
        </Box>
      </Box>
      </Box>
      )}
    </>
  );
};

export default BoletoComponent;
